/**
 * New driver (eskiz 10): 4 ta karta, dublikat tekshiruvi (telefon / CDL), egalik muddati.
 * Forma maydonlari `DriverFormFields` sifatida eksport qilinadi: profil sahifasidagi Edit oynasi ham shuni ishlatadi.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useForm, useController, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle } from "lucide-react";
import { ApiError } from "@/lib/api";
import { dayjs } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { isManager } from "@/features/auth/types";
import { useAppSettings, useCheckDuplicate, useCreateDriver, useRequestTransfer, useUserOptions, type UserOption } from "@/features/drivers/api";
import {
  DRIVER_TYPES,
  DRIVING_TYPES,
  ENDORSEMENTS,
  EQUIPMENT,
  HOME_TIME,
  LEGACY_DRIVER_TYPES,
  SOURCE_LABEL,
  LEGAL_STATUS_LABEL,
  US_STATES,
  type DriverDetail,
  type DriverType,
  type DriverWrite,
  type DuplicateInfo,
  type FreightType,
  LOST_STATUSES,
  type Source,
  type LegalStatus,
} from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Checkbox, Field, Input, NumberInput, Select, Textarea } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";

const SOURCES = Object.keys(SOURCE_LABEL) as Source[];
const LEGAL_STATUSES = Object.keys(LEGAL_STATUS_LABEL) as LegalStatus[];
const phoneOk = (v: string) => {
  const digits = v.replace(/\D/g, "");
  return digits.length >= 10 || (v.trim().startsWith("+") && digits.length >= 8);
};

/**
 * Xato matnlari tarjima qilinadi, shuning uchun sxema t() bilan yasaladi (`useDriverSchema`).
 * Telefon va familiya ixtiyoriy (mijoz jadvalida ko'pida yo'q; telefon yo'qligi ogohlantiriladi).
 * Shtat va CDL klassi -- yangi driverda majburiy; tahrirlashda ixtiyoriy (eski yozuvlarni saqlab bo'lsin).
 */
/** Standart majburiylar (backend `driver_form_required_fields` bilan bir xil); sozlama kelguncha shu */
export const DEFAULT_FORM_REQUIRED = ["state", "cdl_class"];

/** Sozlamadagi majburiy kalit -> forma maydon(lar)i; yaratishda tekshiriladi (tahrirda eski yozuvlar saqlanaversin) */
const REQUIRED_FORM_FIELDS: Record<string, string[]> = {
  phone: ["phone"], email: ["email"], state: ["state"], city: ["city"], date_of_birth: ["date_of_birth"], legal_status: ["legal_status"], cdl_class: ["cdl_class"],
  cdl_state: ["cdl_state"], cdl_number: ["cdl_number"], cdl_expires: ["cdl_expires"], medical_card_expires: ["medical_card_expires"],
  experience_months: ["experience_years", "experience_months"], driver_type: ["driver_type"], freight_type: ["freight_type"],
  endorsements: ["endorsements"], equipment: ["equipment"], home_time: ["home_time"], desired_pay: ["desired_pay"], available_from: ["available_from"],
};

const isBlank = (v: unknown) => v === "" || v === undefined || v === null || (Array.isArray(v) && v.length === 0);

export const makeDriverSchema = (t: TFunction<"drivers">, mode: "create" | "edit" = "create", required: string[] = DEFAULT_FORM_REQUIRED) => z.object({
  first_name: z.string().trim().min(1, t("form.validation.required")),
  last_name: z.string().trim(),
  phone: z.string().trim().refine((v) => v === "" || phoneOk(v), t("form.validation.phone")),
  phone_secondary: z.string().trim().refine((v) => v === "" || phoneOk(v), t("form.validation.phone")),
  email: z.string().trim().refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), t("form.validation.email")),
  state: z.string(),
  city: z.string().trim(),
  date_of_birth: z.string(),
  source: z.string().refine((v) => (SOURCES as string[]).includes(v), t("form.validation.required")),
  referred_by: z.string().trim(),
  legal_status: z.string(),
  cdl_class: z.string().refine((v) => ["A", "B", "C"].includes(v) || v === "", t("form.validation.required")),
  cdl_state: z.string(),
  cdl_number: z.string().trim(),
  cdl_expires: z.string(),
  medical_card_expires: z.string(),
  experience_years: z.string().refine((v) => v === "" || /^\d{1,2}$/.test(v), t("form.validation.years")),
  experience_months: z.string().refine((v) => v === "" || (/^\d{1,2}$/.test(v) && Number(v) < 12), t("form.validation.months")),
  driving_type: z.string(),
  driver_type: z.string(),
  freight_type: z.string(),
  endorsements: z.array(z.string()),
  equipment: z.array(z.string()),
  home_time: z.string(),
  desired_pay: z.string().trim(),
  available_from: z.string(),
  notes: z.string().trim(),
  owner: z.string(),
}).superRefine((v, ctx) => {
  if (mode !== "create") return;
  for (const key of required) {
    const fields = REQUIRED_FORM_FIELDS[key];
    if (!fields) continue;
    const values = fields.map((f) => (v as Record<string, unknown>)[f]);
    if (values.every(isBlank)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [fields[0]], message: t("form.validation.required") });
  }
});

export type DriverFormValues = z.infer<ReturnType<typeof makeDriverSchema>>;

export function useDriverSchema(mode: "create" | "edit" = "create") {
  const { t } = useTranslation("drivers");
  const settings = useAppSettings();
  const required = settings.data?.driver_form_required_fields;
  return useMemo(() => makeDriverSchema(t, mode, required ?? DEFAULT_FORM_REQUIRED), [t, mode, required]);
}

/** Formadagi yulduzcha belgilari uchun: sozlamadagi majburiylar (faqat yaratishda) */
export function useRequiredMarks(mode: "create" | "edit"): Set<string> {
  const settings = useAppSettings();
  const required = settings.data?.driver_form_required_fields;
  return useMemo(() => new Set(mode === "create" ? (required ?? DEFAULT_FORM_REQUIRED) : []), [mode, required]);
}

export const emptyDriverForm: DriverFormValues = {
  first_name: "",
  last_name: "",
  phone: "",
  phone_secondary: "",
  email: "",
  state: "",
  city: "",
  date_of_birth: "",
  source: "",
  referred_by: "",
  legal_status: "",
  cdl_class: "",
  cdl_state: "",
  cdl_number: "",
  cdl_expires: "",
  medical_card_expires: "",
  experience_years: "",
  experience_months: "",
  driving_type: "",
  driver_type: "",
  freight_type: "",
  endorsements: [],
  equipment: [],
  home_time: "",
  desired_pay: "",
  available_from: "",
  notes: "",
  owner: "",
};

export function fromDriverDetail(d: DriverDetail): DriverFormValues {
  const months = d.experience_months ?? null;
  return {
    ...emptyDriverForm,
    first_name: d.first_name,
    last_name: d.last_name,
    phone: d.phone_display || d.phone,
    phone_secondary: d.phone_secondary_display || d.phone_secondary || "",
    email: d.email ?? "",
    state: d.state ?? "",
    city: d.city ?? "",
    date_of_birth: d.date_of_birth ?? "",
    source: d.source,
    referred_by: d.referred_by ?? "",
    legal_status: d.legal_status ?? "",
    cdl_class: d.cdl_class ?? "",
    cdl_state: d.cdl_state ?? "",
    cdl_number: d.cdl_number ?? "",
    cdl_expires: d.cdl_expires ?? "",
    medical_card_expires: d.medical_card_expires ?? "",
    experience_years: months === null ? "" : String(Math.floor(months / 12)),
    experience_months: months === null ? "" : String(months % 12),
    driving_type: d.driving_type ?? "",
    driver_type: d.driver_type ?? "",
    freight_type: d.freight_type ?? "",
    endorsements: d.endorsements ?? [],
    equipment: d.equipment ?? [],
    home_time: d.home_time ?? "",
    desired_pay: d.desired_pay ?? "",
    available_from: d.available_from ?? "",
    notes: d.notes ?? "",
    owner: d.owner ? String(d.owner.id) : "",
  };
}

/** Forma qiymatlari -> API payload. `sensitive=false` bo'lsa recruiter ko'rmaydigan maydonlar yuborilmaydi. */
export function toDriverWrite(v: DriverFormValues, opts: { includeOwner: boolean; sensitive: boolean }): DriverWrite {
  const years = v.experience_years === "" ? null : Number(v.experience_years);
  const months = v.experience_months === "" ? null : Number(v.experience_months);
  const payload: DriverWrite = {
    first_name: v.first_name,
    last_name: v.last_name,
    phone: v.phone,
    phone_secondary: v.phone_secondary,
    email: v.email,
    state: v.state.toUpperCase(),
    city: v.city,
    source: v.source as Source,
    referred_by: v.source === "referral" ? v.referred_by : "",
    legal_status: (v.legal_status || "") as LegalStatus | "",
    cdl_class: v.cdl_class,
    cdl_state: v.cdl_state,
    cdl_expires: v.cdl_expires || null,
    medical_card_expires: v.medical_card_expires || null,
    experience_months: years === null && months === null ? null : (years ?? 0) * 12 + (months ?? 0),
    driving_type: v.driving_type,
    driver_type: v.driver_type as DriverType | "",
    freight_type: v.freight_type as FreightType | "",
    endorsements: v.endorsements,
    equipment: v.equipment,
    home_time: v.home_time,
    desired_pay: v.desired_pay,
    available_from: v.available_from || null,
    notes: v.notes,
  };
  if (opts.sensitive) {
    payload.cdl_number = v.cdl_number;
    payload.date_of_birth = v.date_of_birth || null;
  }
  if (opts.includeOwner) payload.owner = v.owner ? Number(v.owner) : null;
  return payload;
}

export function formatUsPhone(e164: string): string {
  const d = e164.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return e164;
}

function CheckboxGroup({ form, name, options }: { form: UseFormReturn<DriverFormValues>; name: "endorsements" | "equipment"; options: string[] }) {
  const { field } = useController({ control: form.control, name });
  const value = field.value as string[];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {options.map((opt) => (
        <Checkbox
          key={opt}
          label={opt}
          checked={value.includes(opt)}
          onChange={(e) => field.onChange(e.target.checked ? [...value, opt] : value.filter((x) => x !== opt))}
        />
      ))}
    </div>
  );
}

export interface DriverFormFieldsProps {
  form: UseFormReturn<DriverFormValues>;
  mode: "create" | "edit";
  /** Recruiter uchun CDL raqami va tug'ilgan sana yashirin (faqat menejer ko'radi va tahrirlaydi) */
  sensitive: boolean;
  ownerOptions?: UserOption[];
  /** Maydondan chiqqanda dublikat tekshiruvi (yaratishda) */
  onCheckDuplicate?: (field: DupCheckField) => void;
  dupField?: DupField | null;
}

/** Dublikat tekshiruvini ishga tushiradigan maydonlar (name -- ism yoki familiya) */
export type DupCheckField = "phone" | "phone_secondary" | "cdl_number" | "email" | "name";
type DupField = "phone" | "phone_secondary" | "cdl_number" | "email";
// Kuchli moslik turi -> formadagi maydon (backend drivers.services.DUPLICATE_FIELD)
const DUP_FIELD: Record<Exclude<DuplicateInfo["match"], "name">, DupField> = { phone: "phone", phone_secondary: "phone_secondary", cdl: "cdl_number", email: "email" };
// Bazada noyob -- siyosatdan qat'i nazar bloklaydi
const HARD_MATCH: DuplicateInfo["match"][] = ["phone", "cdl"];

export function DriverFormFields({ form, mode, sensitive, ownerOptions, onCheckDuplicate, dupField }: DriverFormFieldsProps) {
  const req = useRequiredMarks(mode);
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const { register, formState, watch } = form;
  const e = formState.errors;
  const source = watch("source");
  const phone = watch("phone") ?? "";
  const driverType = watch("driver_type");
  const err = (k: keyof DriverFormValues) => e[k]?.message as string | undefined;
  const inv = (k: keyof DriverFormValues) => (e[k] ? true : undefined);

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader title={t("form.contact")} />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.firstName")} required error={err("first_name")} htmlFor="first_name">
              <Input id="first_name" autoFocus={mode === "create"} aria-invalid={inv("first_name")} {...register("first_name", { onBlur: () => onCheckDuplicate?.("name") })} />
            </Field>
            <Field label={t("form.lastName")} error={err("last_name")} htmlFor="last_name">
              <Input id="last_name" aria-invalid={inv("last_name")} {...register("last_name", { onBlur: () => onCheckDuplicate?.("name") })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.phone")} required={req.has("phone")} error={err("phone")} htmlFor="phone" hint={dupField === "phone" || !phone.trim() ? undefined : t("form.phoneHint")}>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="(555) 010-0142"
                aria-invalid={inv("phone")}
                {...register("phone", { onBlur: () => onCheckDuplicate?.("phone") })}
              />
            </Field>
            <Field label={t("form.email")} required={req.has("email")} error={err("email")} htmlFor="email">
              <Input id="email" type="email" placeholder="name@example.com" aria-invalid={inv("email")} {...register("email", { onBlur: () => onCheckDuplicate?.("email") })} />
            </Field>
          </div>
          {/* Telefon majburiy emas (foydalanuvchi qarori), lekin yo'qligi kichik ogohlantirish bilan ko'rsatiladi */}
          {!phone.trim() && !err("phone") && (
            <p className="-mt-1 flex items-center gap-1.5 text-[12px] text-warning">
              <AlertTriangle size={13} /> {t("form.noPhoneWarning")}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.state")} required={req.has("state")} error={err("state")} htmlFor="state">
              <Select id="state" aria-invalid={inv("state")} {...register("state")}>
                <option value="">{t("form.select")}</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("form.city")} required={req.has("city")} htmlFor="city">
              <Input id="city" placeholder="Houston" {...register("city")} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.secondaryPhone")} error={err("phone_secondary")} htmlFor="phone_secondary">
              <Input id="phone_secondary" type="tel" aria-invalid={inv("phone_secondary")} {...register("phone_secondary", { onBlur: () => onCheckDuplicate?.("phone_secondary") })} />
            </Field>
            <Field label={t("form.legalStatus")} required={req.has("legal_status")} error={err("legal_status")} htmlFor="legal_status">
              <Select id="legal_status" {...register("legal_status")}>
                <option value="">{t("form.select")}</option>
                {LEGAL_STATUSES.map((ls) => (
                  <option key={ls} value={ls}>
                    {tc(`legalStatus.${ls}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {mode === "edit" && sensitive && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("form.dob")} required={req.has("date_of_birth")} htmlFor="date_of_birth" hint={t("form.dobHint")}>
                <Input id="date_of_birth" type="date" {...register("date_of_birth")} />
              </Field>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t("form.cdlMedical")} />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.cdlClass")} required={req.has("cdl_class")} error={err("cdl_class")} htmlFor="cdl_class">
              <Select id="cdl_class" aria-invalid={inv("cdl_class")} {...register("cdl_class")}>
                <option value="">{t("form.select")}</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </Select>
            </Field>
            <Field label={t("form.cdlState")} required={req.has("cdl_state")} htmlFor="cdl_state">
              <Select id="cdl_state" {...register("cdl_state")}>
                <option value="">{t("form.select")}</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {sensitive ? (
              <Field label={t("form.cdlNumber")} required={req.has("cdl_number")} error={err("cdl_number")} htmlFor="cdl_number" hint={dupField === "cdl_number" ? undefined : t("form.cdlNumberHint")}>
                <Input
                  id="cdl_number"
                  placeholder={t("form.cdlNumberPlaceholder")}
                  aria-invalid={inv("cdl_number")}
                  {...register("cdl_number", { onBlur: () => onCheckDuplicate?.("cdl_number") })}
                />
              </Field>
            ) : (
              <Field label={t("form.cdlNumber")} required={req.has("cdl_number")} hint={t("form.managedByManager")}>
                <Input disabled value="••••••" readOnly />
              </Field>
            )}
            <Field label={t("form.cdlExpires")} required={req.has("cdl_expires")} htmlFor="cdl_expires">
              <Input id="cdl_expires" type="date" {...register("cdl_expires")} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.medicalExpires")} required={req.has("medical_card_expires")} htmlFor="medical_card_expires">
              <Input id="medical_card_expires" type="date" {...register("medical_card_expires")} />
            </Field>
            <Field label={t("form.endorsements")} required={req.has("endorsements")}>
              <CheckboxGroup form={form} name="endorsements" options={ENDORSEMENTS} />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t("form.experienceTitle")} />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.experience")} required={req.has("experience_months")} error={err("experience_years") ?? err("experience_months")}>
              <div className="flex items-center gap-2">
                <NumberInput className="w-20" min={0} placeholder="3" aria-invalid={inv("experience_years")} {...register("experience_years")} />
                <span className="text-[12px] text-muted">{t("form.years")}</span>
                <NumberInput className="w-20" min={0} placeholder="0" aria-invalid={inv("experience_months")} {...register("experience_months")} />
                <span className="text-[12px] text-muted">{t("form.months")}</span>
              </div>
            </Field>
            <Field label={t("form.drivingType")} htmlFor="driving_type">
              <Select id="driving_type" {...register("driving_type")}>
                <option value="">{t("form.select")}</option>
                {DRIVING_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {/* Mijoz ishining asosiy o'lchovlari: driver turi va yuk turi (jadvaldagi "Type of drivers", "Recruiting type") */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.driverType")} required={req.has("driver_type")} htmlFor="driver_type">
              <Select id="driver_type" {...register("driver_type")}>
                <option value="">{t("form.select")}</option>
                {[...DRIVER_TYPES, ...LEGACY_DRIVER_TYPES.filter((x) => x === driverType)].map((x) => (
                  <option key={x} value={x}>
                    {tc(`driverType.${x}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("form.freight")} required={req.has("freight_type")} htmlFor="freight_type">
              <Select id="freight_type" {...register("freight_type")}>
                <option value="">{t("form.select")}</option>
                {(["amazon", "street", "both"] as const).map((x) => (
                  <option key={x} value={x}>
                    {tc(`freight.${x}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label={t("form.equipment")} required={req.has("equipment")}>
            <CheckboxGroup form={form} name="equipment" options={EQUIPMENT} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.homeTime")} required={req.has("home_time")} htmlFor="home_time">
              <Select id="home_time" {...register("home_time")}>
                <option value="">{t("form.select")}</option>
                {HOME_TIME.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("form.desiredPay")} required={req.has("desired_pay")} htmlFor="desired_pay">
              <Input id="desired_pay" placeholder={t("form.desiredPayPlaceholder")} {...register("desired_pay")} />
            </Field>
          </div>
          <Field label={t("form.availableFrom")} required={req.has("available_from")} htmlFor="available_from">
            <Input id="available_from" type="date" {...register("available_from")} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t("form.sourceTitle")} />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("form.source")} required error={err("source")} htmlFor="source">
              <Select id="source" aria-invalid={inv("source")} {...register("source")}>
                <option value="">{t("form.select")}</option>
                {SOURCES.filter((s) => mode === "edit" || (s !== "import" && s !== "apply_form")).map((s) => (
                  <option key={s} value={s}>
                    {tc(`source.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("form.referredBy")} htmlFor="referred_by">
              <Input id="referred_by" placeholder={t("form.referredByPlaceholder")} disabled={source !== "referral"} {...register("referred_by")} />
            </Field>
          </div>
          {ownerOptions && (
            <Field label={t("form.owner")} htmlFor="owner" hint={t("form.ownerHint")}>
              <Select id="owner" {...register("owner")}>
                <option value="">{t("form.pool")}</option>
                {ownerOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t("form.notes")} htmlFor="notes">
            <Textarea id="notes" rows={4} placeholder={t("form.notesPlaceholder")} {...register("notes")} />
          </Field>
        </CardBody>
      </Card>
    </div>
  );
}

export function DuplicateBanner({ dup, onRequestTransfer, requesting, meId }: { dup: DuplicateInfo; onRequestTransfer: () => void; requesting: boolean; meId?: number }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const what =
    dup.match === "phone" ? t("dup.phone", { phone: formatUsPhone(dup.phone) }) : dup.match === "phone_secondary" ? t("dup.phoneSecondary") : dup.match === "email" ? t("dup.email") : t("dup.cdl");
  const lost = dup.status && LOST_STATUSES.includes(dup.status);
  return (
    <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-[10px] border border-danger bg-danger-soft px-4 py-3 text-[13px]">
      <AlertTriangle size={16} className="text-danger" />
      <div className="flex-1">
        {/* Scam -- birinchi qatorda, qalin: qayta ishga olishdan oldin ko'rinishi shart (2026-10-25) */}
        {dup.scam && (
          <div className="mb-1 font-bold text-danger">
            {t("dup.scam", { date: dayjs(dup.scam.at).format("MMM D, YYYY") })}
            {dup.scam.by ? ` ${t("dup.scamBy", { name: dup.scam.by })}` : ""}
          </div>
        )}
        <span className="font-semibold">{t("dup.title")}</span> {what} {t("dup.belongsTo")} <span className="font-semibold">{dup.full_name}</span>
        {dup.owner ? ` · ${t("dup.owner", { name: dup.owner.display_name })}` : ` · ${t("dup.unassigned")}`}
        {dup.assigned_until && !lost ? ` · ${t("dup.assignedUntil", { date: dayjs(dup.assigned_until).format("MMM D") })}` : ""}
        {lost && dup.status && (
          // Ketgan / rad etilgan: yangi driver emas -- o'sha profilni qayta ochish kerak (Contacted yoki Qualified)
          <div className="mt-1 text-ink-soft">
            {t("dup.lost", {
              status: tc(`status.${dup.status}`),
              reason: dup.status_reason || "—",
              date: dup.status_changed_at ? dayjs(dup.status_changed_at).format("MMM D, YYYY") : "—",
            })}{" "}
            {dup.can_open ? t("dup.reopenHint", { a: tc("status.contacted"), b: tc("status.qualified") }) : ""}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {dup.can_open && (
          <Link to={`/drivers/${dup.id}`}>
            <Button size="sm">{t("dup.open")}</Button>
          </Link>
        )}
        {/* O'zining driveri -- o'tkazish so'rovi ma'nosiz */}
        {dup.owner && dup.owner.id !== meId && (
          <Button size="sm" variant="primary" loading={requesting} onClick={onRequestTransfer}>
            {t("dup.askTransfer")}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Faqat ism-familiya mosligi: ogohlantirish (bir xil ismli boshqa odam bo'lishi mumkin) -- har biri ochish / o'tkazish so'rovi bilan */
export function NameMatchesBanner({ matches, meId, onRequestTransfer, requestingId }: { matches: DuplicateInfo[]; meId?: number; onRequestTransfer: (id: number) => void; requestingId: number | null }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  return (
    <div role="alert" className="mb-4 rounded-[10px] border border-warning/40 bg-warning-soft px-4 py-3 text-[13px]">
      <div className="mb-2 flex items-center gap-2 font-semibold text-warning">
        <AlertTriangle size={16} aria-hidden /> {t("dup.sameName", { count: matches.length })}
      </div>
      <ul className="space-y-1.5">
        {matches.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="min-w-0 flex-1">
              <span className="font-semibold">{m.full_name}</span>
              {m.status ? ` · ${tc(`status.${m.status}`)}` : ""}
              {m.status && LOST_STATUSES.includes(m.status) && m.status_reason ? ` (${m.status_reason})` : ""}
              {m.owner ? ` · ${t("dup.owner", { name: m.owner.display_name })}` : ` · ${t("dup.unassigned")}`}
              {m.scam && <span className="ml-2 font-bold text-danger">{t("dup.scamShort", { date: dayjs(m.scam.at).format("MMM D, YYYY") })}</span>}
            </span>
            {m.can_open && (
              <Link to={`/drivers/${m.id}`}>
                <Button size="sm">{t("dup.open")}</Button>
              </Link>
            )}
            {m.owner && m.owner.id !== meId && (
              <Button size="sm" loading={requestingId === m.id} onClick={() => onRequestTransfer(m.id)}>
                {t("dup.askTransfer")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NewDriverPage() {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const driverSchema = useDriverSchema();
  const navigate = useNavigate();
  const me = useMe().data;
  const lead = me ? isManager(me.role) : false;
  const settings = useAppSettings();
  const owners = useUserOptions({ role: "recruiter" }, lead);
  const create = useCreateDriver();
  const check = useCheckDuplicate();
  const requestTransfer = useRequestTransfer();
  const [dup, setDup] = useState<DuplicateInfo | null>(null);
  const [names, setNames] = useState<DuplicateInfo[]>([]);
  const [policy, setPolicy] = useState<"block" | "warn">("block");
  // "Bu boshqa odam -- baribir qo'shish": moslik o'zgarsa qaytadan so'raladi
  const [confirmed, setConfirmed] = useState(false);
  const [requestedIds, setRequestedIds] = useState<number[]>([]);

  const form = useForm<DriverFormValues>({ resolver: zodResolver(driverSchema), defaultValues: emptyDriverForm, mode: "onBlur" });
  const { handleSubmit, getValues, setError, clearErrors, watch } = form;
  const ownerValue = watch("owner");

  // Kuchli moslik bloklaydimi: telefon va CDL -- doim, qolganlari -- siyosatga qarab (backend bilan bir xil qoida)
  const blocked = dup !== null && (HARD_MATCH.includes(dup.match) || policy === "block");
  const needsConfirm = (dup !== null && !blocked) || (dup === null && names.length > 0);

  /** Hamma to'ldirilgan maydon bilan bitta so'rov: kuchli moslik, ism mosliklari va shu foydalanuvchi uchun siyosat */
  async function checkDuplicate(field?: DupCheckField) {
    const v = getValues();
    if ((field === "phone" && v.phone && !phoneOk(v.phone)) || (field === "phone_secondary" && v.phone_secondary && !phoneOk(v.phone_secondary))) return;
    try {
      const res = await check.mutateAsync({
        phone: phoneOk(v.phone) ? v.phone : undefined,
        phone_secondary: phoneOk(v.phone_secondary) ? v.phone_secondary : undefined,
        cdl_number: v.cdl_number || undefined,
        email: v.email || undefined,
        first_name: v.first_name || undefined,
        last_name: v.last_name || undefined,
      });
      const ids = (d: DuplicateInfo | null, n: DuplicateInfo[]) => [d?.id ?? 0, ...n.map((m) => m.id)].join(",");
      if (ids(res.duplicate, res.name_matches) !== ids(dup, names)) setConfirmed(false);
      setDup(res.duplicate);
      setNames(res.name_matches);
      setPolicy(res.policy);
      clearErrors(["phone", "phone_secondary", "cdl_number", "email"]);
      if (res.duplicate && res.duplicate.match !== "name") {
        setError(DUP_FIELD[res.duplicate.match], { type: "duplicate", message: t("form.validation.duplicate") });
      }
    } catch {
      /* tekshiruv ishlamasa, saqlashda backend baribir tekshiradi */
    }
  }

  async function onSubmit(values: DriverFormValues) {
    try {
      const driver = await create.mutateAsync({ ...toDriverWrite(values, { includeOwner: lead, sensitive: true }), confirm_duplicate: needsConfirm && confirmed });
      toast.success(t("new.added"), t("new.addedDesc", { name: driver.full_name }));
      navigate(`/drivers/${driver.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields) {
          for (const [k, msgs] of Object.entries(err.fields)) {
            if (k in emptyDriverForm) setError(k as keyof DriverFormValues, { type: "server", message: msgs.join(" ") });
          }
          if (err.fields.duplicate) await checkDuplicate();
        }
        toast.error(err.fields ? t("new.checkFields") : err.message);
      } else {
        toast.error(t("new.saveFailed"));
      }
    }
  }

  async function askTransfer(id: number) {
    try {
      await requestTransfer.mutateAsync({ id, note: t("new.transferNote") });
      setRequestedIds((x) => [...x, id]);
      toast.success(t("new.requestSent"), t("new.requestSentDesc"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("new.requestFailed"));
    }
  }

  const days = Number(settings.data?.assignment_days ?? 30);
  const ownerLabel = !lead
    ? (me?.display_name ?? t("new.you"))
    : ownerValue
      ? (owners.data?.results.find((u) => String(u.id) === ownerValue)?.display_name ?? t("new.selectedRecruiter"))
      : t("new.pool");

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="mb-1 text-[12px] text-muted">
        <Link to="/drivers" className="hover:underline">
          {t("new.breadcrumb")}
        </Link>{" "}
        / <span className="font-semibold text-ink">{t("new.title")}</span>
      </div>
      <PageHeader title={t("new.title")} />
      {dup && <DuplicateBanner dup={dup} meId={me?.id} onRequestTransfer={() => askTransfer(dup.id)} requesting={requestTransfer.isPending || requestedIds.includes(dup.id)} />}
      {!dup && names.length > 0 && (
        <NameMatchesBanner matches={names} meId={me?.id} onRequestTransfer={askTransfer} requestingId={requestTransfer.isPending ? (requestTransfer.variables?.id ?? null) : null} />
      )}
      {needsConfirm && (
        // Siyosat ruxsat bersa: ongli tasdiq (backend ham talab qiladi va audit'ga yozadi)
        <div className="-mt-2 mb-4">
          <Checkbox label={t("dup.confirm")} checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        </div>
      )}
      <DriverFormFields
        form={form}
        mode="create"
        sensitive
        ownerOptions={lead ? (owners.data?.results ?? []) : undefined}
        onCheckDuplicate={checkDuplicate}
        dupField={dup && dup.match !== "name" ? DUP_FIELD[dup.match] : null}
      />
      <div className="mt-4 flex items-center justify-between gap-4 border-t border-line pt-4 text-[12px] text-muted">
        <div>
          {t("new.ownerAfter")} <span className="font-semibold text-ink">{ownerLabel}</span>
          {(ownerValue || !lead) && (
            <>
              {" "}
              · {t("new.assignedFor", { days, until: dayjs().add(days, "day").format("MMM D") })}
            </>
          )}
          . <span className="text-danger">*</span> {t("new.required")}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/drivers")}>{tc("actions.cancel")}</Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={blocked || (needsConfirm && !confirmed)}>
            {t("new.save")}
          </Button>
        </div>
      </div>
    </form>
  );
}
